import { emitEvent } from './events';

const componentKey = 'private-seller';

const appKey = 'psx-listing-actions';

const ANALYTICS_EVENT_NAME = 'TrackingEvent';

interface IPayloadGeneratorParams {
  event?: Event,
  eventResult: string,
  eventData?: object,
}
const commonPayloadGenerator = (type: 'click' | 'impression' | 'submit', {
  event,
  eventResult,
  eventData,
}: IPayloadGeneratorParams, extraGoogleAnalyticsProps?: object, customDataProps?: object) => ({
  event: 'analytics_event',
  eventElement: componentKey,
  eventAction: type,
  eventResult,
  eventNonInteractive: type === 'impression',
  eventData,
  ...extraGoogleAnalyticsProps,
  customData: {
    appKey,
    browserEvent: event,
    ...customDataProps,
  },
});

const dataLayer = globalThis.dataLayer ||= [];
export const sendPayload = (payload: object, pushToDataLayer?: boolean) => {
  emitEvent(ANALYTICS_EVENT_NAME, payload);
  if (pushToDataLayer) {
    dataLayer.push(payload);
  }
  return payload;
};

export const sendAnalytics: (payload: ReturnType<typeof commonPayloadGenerator>) => {} = sendPayload;

const click = (eventResult: string, eventData?: object) => (event: Event, extraGoogleAnalyticsProps?: object, customDataProps?: object) =>
  sendAnalytics(commonPayloadGenerator('click', { event, eventResult, eventData }, extraGoogleAnalyticsProps, customDataProps));

const submit = (eventResult: string, eventData?: object) => (event: Event, extraGoogleAnalyticsProps?: object, customDataProps?: object) => {
  sendAnalytics(commonPayloadGenerator('submit', { event, eventResult, eventData }, extraGoogleAnalyticsProps, customDataProps));
};

const psxOfferBaseObject = {
  eventType: 'click',
  vehicleType: 'used',
  program: 'DealerDotCom',
  pageId: 'psx_offer-details',
  pageType: 'psx',
  feature: 'psx-offers',
  listingType: 'pvt',
};

export const analytics = {
  // Make Offer Events
  MAKE_OFFER_BUTTON_CTA: (event: Event, eventData?: object, pixallProps?: any, gtmProps?: any) => click('cta::make-offer', eventData)(event, gtmProps, {
    pixall: { label: 'make_off', eventSource: 'purchase-offer', eventType: 'start', ...pixallProps },
    segment: { eventName: 'Buy - Clicked Make Offer' },
  }),
  START_PURCHASE_BUTTON_CTA: (event: Event, eventData?: object, pixallProps?: any, gtmProps?: any) => click('cta::open::buy-now', eventData)(event, gtmProps, {
    pixall: { label: 'buy_now', eventSource: 'sale', eventType: 'start', ...pixallProps },
    segment: { eventName: 'Buy - Buy Now Button - Clicked Buy Now Button' },
  }),
  MAKE_OFFER_CONTENT_CTA: (event: Event, eventData?: object, segmentProps?: any, pixallProps?: any, isOfferDetails: boolean = false) => submit('cta::send-offer::make-offer', eventData)(event, null, {
    pixall: { label: 'cta::send-offer::make-offer', ...(isOfferDetails ? psxOfferBaseObject : { eventSource: 'purchase-offer' }), eventType: 'submit', ...pixallProps },
    segment: { eventName: 'Buy - Made an Offer', ...segmentProps },
  }),
  VIEWED_SELLER_TAKE_HOME_BREAKDOWN: (event: Event, eventData?: object, segmentProps?: any) => click('cta::offer::view-offer', eventData)(event, null, {
    pixall: { label: 'cta::offer::view-offer', eventSource: 'psx-offer', eventType: 'click' },
    segment: { eventName: 'Dashboard - Sell - Viewed Take Home Breakdown', ...segmentProps },
  }),
  MAKE_OFFER_CONTENT_MY_OFFER_CHANGE: (event: Event, eventData?: object, segmentProps?: any, pixallProps?: any, isOfferDetails: boolean = false) => click('cta::change-my-offer', eventData)(event, null, {
    pixall: { label: 'cta::change-my-offer', ...(isOfferDetails ? psxOfferBaseObject : { eventSource: 'purchase-offer' }), eventType: 'step', ...pixallProps },
    segment: { eventName: 'Buy - Entered an Offer', ...segmentProps },
  }),
  WHAT_DO_I_GET_WITH_THIS_OFFER_CTA: (event: Event, eventData?: object, pixallProps?: any) => click('text-link::what-do-i-get-with-this-offer', eventData)(event, null, {
    pixall: { label: 'text-link::what-do-i-get-with-this-offer', ...psxOfferBaseObject, ...pixallProps },
  }),
  OFFER_ACCEPTED_CTA: (event: Event, eventData?: object, pixallProps?: any, userType?: string) => click(`cta::offer-accepted::${userType}`, eventData)(event, null, {
    pixall: { label: `cta::offer-accepted::${userType}`, ...psxOfferBaseObject, ...pixallProps },
  }),
  COUNTEROFFER_CTA: (event: Event, eventData?: object, pixallProps?: any, userType?: string) => click(`cta::counteroffer::${userType}`, eventData)(event, null, {
    pixall: { label: `cta::counteroffer::${userType}`, ...psxOfferBaseObject, ...pixallProps },
  }),
  SUBMIT_OFFER_CTA: (event: Event, eventData?: object, pixallProps?: any, userType?: string) => click(`cta::submit-offer::${userType}`, eventData)(event, null, {
    pixall: { label: `cta::submit-offer::${userType}`, ...psxOfferBaseObject, ...pixallProps },
  }),
  CHANGE_MY_MIND_AND_ACCEPT_THE_OFFER_CTA: (event: Event, eventData?: object, pixallProps?: any, userType?: string) => click(`cta::change-my-mind-and-accept-the-offer::${userType}`, eventData)(event, null, {
    pixall: { label: `cta::change-my-mind-and-accept-the-offer::${userType}`, ...psxOfferBaseObject, ...pixallProps },
  }),
  BUY_NOW_CTA: (event: Event, eventData?: object, pixallProps?: any) => click(`buy_now`, eventData)(event, null, {
    pixall: { label: `buy_now`, ...psxOfferBaseObject, ...pixallProps },
  }),
  YES_ACCEPT_OFFER_CTA: (event: Event, eventData?: object, pixallProps?: any, userType?: string) => click(`cta::yes-accept-offer::${userType}`, eventData)(event, null, {
    pixall: { label: `cta::yes-accept-offer::${userType}`, ...psxOfferBaseObject, ...pixallProps },
  }),
  RESCIND_OFFER_CTA: (event: Event, eventData?: object, pixallProps?: any, userType?: string) => click(`cta::rescind::${userType}`, eventData)(event, null, {
    pixall: { label: `cta::rescind::${userType}`, ...psxOfferBaseObject, ...pixallProps },
  }),
  // Test Drive Events
  TEST_DRIVE_BUTTON_CTA: (event: Event, eventData?: object, pixallProps?: any, gtmProps?: any) => click('cta::open::test-drive', { ...eventData, vehicle: { productConfiguration: 'NDS' } })(event, gtmProps, {
    pixall: { label: 'test_drv', eventSource: 'test-drive', ...pixallProps },
    segment: { eventName: 'Buy - Clicked Schedule Test Drive' },
  }),
  TEST_DRIVE_REQUEST_OLD: (event: Event, eventData?: object, segmentProps?: any) => submit('cta::submit::request-test-drive', { ...eventData, vehicle: { productConfiguration: 'NDS|schedule-test-drive' } })(event, null, {
    pixall: { label: 'cta::submit::request-test-drive', eventSource: 'test-drive', eventType: 'submit' },
    segment: { eventName: 'Buy - Scheduled Test Drive', ...segmentProps },
  }),
  VIEW_EDIT_TEST_DRIVE_CTA: (event: Event, eventData?: object) => click('cta::view-or-edit-test-drive', eventData)(event, null, {
    pixall: { label: 'cta::view-or-edit-test-drive', eventSource: 'test-drive', feature: 'private-seller' },
  }),
  // Chat Seller Events
  CHAT_SELLER_BUTTON_CTA: (event: Event, eventData?: object) => {
    submit('lead::chat', { ...eventData })(event, null, {
      pixall: { label: 'cs_chat', eventSource: 'lead', feature: 'private-seller', leadType: 'chat', cdl: { eventProperties: { event: { valueEventCode: 'GS6F' } } } },
      segment: { eventName: 'Buy - Clicked Ask Seller a Question' },
    })
  },
  CHAT_SELLER_BUTTON_CTA_LOGGEDOUT: (event: Event, eventData?: object) => 
    click('logout::chat', eventData)(event, null, {
  }),
  TEST_DRIVE_REQUEST: (event: Event, userType: string, eventData?: object, segmentProps?: object) => submit('cta::submit::request-test-drive', { ...eventData, vehicle: { productConfiguration: 'NDS|schedule-test-drive' } })(event, null, {
    pixall: { label: `cta::submit::request-test-drive::${userType}`, eventSource: 'vehicle', pageId: 'psx-test-drive', eventType: 'submit' },
    segment: { eventName: 'Buy - Scheduled Test Drive', ...segmentProps },
  }),
  TEST_DRIVE_ADD_DATE_TIME: (event: Event, userType: string, eventData?: object) => click('text-link::add-an-alternate-date-and-time', eventData)(event, null, {
    pixall: { label: `text-link::add-an-alternate-date-and-time::${userType}`, eventSource: 'vehicle', pageId: 'psx-test-drive' },
  }),
  TEST_DRIVE_GET_DIRECTIONS: (event: Event, userType: string, eventData?: object) => click('cta::get-directions', eventData)(event, null, {
    pixall: { label: `cta::get-directions::${userType}`, eventSource: 'vehicle', pageId: 'psx-test-drive' },
  }),
  TEST_DRIVE_GOTO_DASHBOARD: (event: Event, userType: string, eventData?: object) => click('cta::go-to-dashboard', eventData)(event, null, {
    pixall: { label: `cta::go-to-dashboard::${userType}`, eventSource: 'vehicle', pageId: 'psx-test-drive' },
  }),

  // Lead Generation (Aggregator) Events
  BUYER_GUIDE_LINK: (event: Event, eventData?: object) => click('text-link::buyer-guide', eventData)(event, null, {
    pixall: { label: 'buyer_guide' },
  }),
  SIGN_UP_CTA: (event: Event, eventData?: object) =>
    click('cta::register-start', eventData)(event, { ...eventData }, {
      pixall: { label: 'cta::register-start' },
    }),
  SIGN_UP_INFO_LINK: (event: Event, eventData?: object) => click('cta::register-start', eventData)(event, { ...eventData }, {
    pixall: { label: 'cta::register-start' },
  }),
  // Segment Events
  SEND_SEGMENT: (segmentData?: object) => sendPayload({
    event: 'analytics_event',
    customData: {
      appKey,
      segment: segmentData
    },
  }),
};
